import {
  Button,
  Card,
  CardContent,
  createStyles,
  FormControl,
  FormHelperText,
  InputAdornment,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import React, {ReactElement, useState} from 'react';
import NavRoutes from 'src/constants/route/NavRoutes';
import {
  APP,
  BLACK_COLOR,
  BORDER_COLOR,
  GET_8_PIXEL_BOX
} from 'src/style/variable';
import HomeNavigation from '../backNavigation/HomeNavigation';
import {useSnackbar} from 'notistack';

import ThanksLogo from '@components/system/MyIcons/ThanksLogo';
import './ThanksScreenStyles.scss';
import {useForm, Controller} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import PaymentsStyledInput from '@components/system/FormInput/PaymentsStyledInput';
import EmailIcon from '@components/system/MyIcons/EmailIcon';
import {
  emailValidationWithHookForm,
  emailValidationWithThanksHookForm
} from '@shared/modules/payments/Success/Succes';
import CurrencyFormatter from '@components/system/CurrencyFormatter/CurrencyFormatter';
import {useDispatch, useSelector} from 'react-redux';
import {
  gurantorDownloadPaymentReceipt,
  instaMedSendPaymentDetailsMail
} from '@shared/services/PaymentProvider.service';
import {SnackBarConfig} from '../../../utils/SnackBarConfig';
import {
  downloadPaymentReceipt,
  sendConfirmationMail
} from '@shared/services/PaymentHistoryService';
import {PaymentTypes} from '@shared/constants/Mapping';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import HelperUtils, {formFileName} from '@shared/utils/HelperUtils';
import {downloadFile} from 'src/utils/WebHelperUtils';
import PayInPaymentSuccessDetails from './PayInPaymentSuccessDetails';
import {_setPayInInstallmentDetails} from '@shared/store/actions/PaymentActions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    EmailButton: {
      padding: `0 ${GET_8_PIXEL_BOX(1)}`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '3.5rem',
      lineHeight: '1.4rem',
      marginLeft: '0.5rem'
    },
    card: {
      margin: 'auto',
      height: '100%',
      border: `1px solid ${BORDER_COLOR}`,
      position: 'relative',
      borderRadius: 0,
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      boxShadow: 'none'
    },
    cardPadding: {
      '& .MuiCardContent-root': {
        padding: 0
      },
      padding: 0,
      overflow: 'auto',
      display: 'flex',
      flexDirection: 'column'
    },
    fieldText: {
      display: 'flex',
      color: BLACK_COLOR,
      paddingBottom: '0.5rem',
      justifyContent: 'flex-start',
      fontSize: '1.2rem'
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between'
    },
    emailContainer: {
      width: '70%',
      marginRight: '1rem'
    },
    buttonContainer: {width: '30%'}
  })
);
// TODO: Declare interface here if required and Give proper types to the below interface
const ThanksScreen = (props: any): ReactElement => {
  const classes = useStyles();
  // const [loader, setLoader] = useState<boolean>(false);
  const {history, match, isAdmin, clinicId, clinicGroupId} = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const token = match.params?.token;
  const code = match.params?.code;
  // const [emails,setEmails]: any = useState([]);
  const {control, handleSubmit, errors, getValues} = useForm({
    mode: 'all',
    resolver: async values => {
      const emailValues: any = getValues('email').split(',');

      return {
        values,
        errors:
          emailValues.length === 1
            ? emailValidationWithHookForm(values)
            : emailValidationWithThanksHookForm(emailValues)
      };
    }
  });
  const clinicDetails = useSelector(
    (state: any) => state.payments.clinicDetails
  );
  const guarantorPaymentInfo = useSelector(
    (state: any) => state.payments.guarantorPaymentResult
  );
  const guarantorDetails = useSelector(
    (state: any) =>
      state.payments.clinicDetails?.guarantor_details ??
      state?.payments?.guarantorDetails
  );
  const paymentTypeRedux = useSelector(
    (state: any) =>
      state?.payments?.paymentType ?? PaymentTypes.STATEMENT_PAYMENT
  );
  const [loader, setLoader] = useState<boolean>(false);
  const [downloadReceiptLoader, setDownloadReceiptLoader] = useState<boolean>(
    false
  );

  const installmentDetails = useSelector(
    (state: any) => state.payments.installmentDetails
  );
  const dispatch = useDispatch();
  const backNavigation = (): void => {
    const details = {
      instalment_start_date: null,
      min_instalment_amount: null,
      total_installments: null,
      pay_in_installments_selected: false,
      next_installment_date: null,
      plan_frequency: '',
      payment_plan_start_date: null
    };
    dispatch(_setPayInInstallmentDetails(details));

    if (paymentTypeRedux === PaymentTypes.PUBLIC_AD_HOC_PATIENT_PAYMENT) {
      history.push(`/${NavRoutes.PUBLIC.PAYMENT_PRACTICE}/${code}`);
    } else {
      history?.push(`/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${token}`);
    }
  };

  React.useEffect(() => {
    if (
      !isAdmin &&
      (!guarantorPaymentInfo || !guarantorPaymentInfo.amount) &&
      !installmentDetails?.pay_in_installments_selected
    ) {
      backNavigation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleFormSubmit = async (values: any): Promise<void> => {
    const emailValues: any = getValues('email').split(',');
    try {
      setLoader(true);
      if (isAdmin) {
        if (emailValues.length === 1) {
          const response = await sendConfirmationMail(
            clinicGroupId,
            clinicId,
            guarantorPaymentInfo.invoice_number,
            {email: values.email.trim(), thanks_screen: true}
          );
          if (response) {
            enqueueSnackbar(
              t('PAYMENTS_LOGIN.DASHBOARD.SEND_PAYMENT_DETAILS'),
              SnackBarConfig('s')
            );
          }
        } else {
          for (let i = 0; i < emailValues.length; i += 1) {
            const response = await sendConfirmationMail(
              clinicGroupId,
              clinicId,
              guarantorPaymentInfo.invoice_number,
              {email: emailValues[i].trim(), thanks_screen: true}
            );
            if (response) {
              enqueueSnackbar(
                t('PAYMENTS_LOGIN.DASHBOARD.SEND_PAYMENT_DETAILS'),
                SnackBarConfig('s')
              );
            }
          }
        }
      } else {
        if (emailValues.length === 1) {
          const response = await instaMedSendPaymentDetailsMail({
            token: token,
            body: {
              email: values.email.trim(),
              payment_id: guarantorPaymentInfo.payment_id,
              invoice_number: guarantorPaymentInfo.invoice_number
            }
          });
          if (response) {
            enqueueSnackbar(
              t('PAYMENTS_LOGIN.DASHBOARD.SEND_PAYMENT_DETAILS'),
              SnackBarConfig('s')
            );
          }
        } else {
          for (let i = 0; i < emailValues.length; i += 1) {
            const response = await instaMedSendPaymentDetailsMail({
              token: token,
              body: {
                email: emailValues[i].trim(),
                payment_id: guarantorPaymentInfo.payment_id,
                invoice_number: guarantorPaymentInfo.invoice_number
              }
            });
            if (response) {
              enqueueSnackbar(
                t('PAYMENTS_LOGIN.DASHBOARD.SEND_PAYMENT_DETAILS'),
                SnackBarConfig('s')
              );
            }
          }
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      throw error;
    } finally {
      setLoader(false);
    }
  };

  // COMMENTED DOWNLOAD PAYMENT RECEIPT FUNCTION.
  const handleDownloadPaymentReceipt = async (
    selectedData: any
  ): Promise<void> => {
    try {
      setDownloadReceiptLoader(true);

      if (isAdmin) {
        const response = await downloadPaymentReceipt(
          clinicGroupId,
          clinicId,
          guarantorPaymentInfo.invoice_number
        );
        if (response) {
          setLoader(false);
          downloadFile(
            `data:application/pdf;base64,${response.invoice}`,
            formFileName(
              'Payment Receipt',
              clinicDetails?.guarantor_details?.first_name || '',
              clinicDetails?.guarantor_details?.last_name || '',
              clinicDetails?.clinic_details?.name || '',
              guarantorPaymentInfo.invoice_number || ''
            ) + '.pdf'
          );
        }
      } else {
        const response = await gurantorDownloadPaymentReceipt({
          invoice_number: guarantorPaymentInfo.invoice_number
        });
        if (response) {
          setLoader(false);
          downloadFile(
            `data:application/pdf;base64,${response.invoice}`,
            formFileName(
              'Payment Receipt',
              clinicDetails?.guarantor_details?.name || '',
              clinicDetails?.clinic_details?.name || '',
              guarantorPaymentInfo.invoice_number || ''
            ) + '.pdf'
          );
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
      throw error;
    } finally {
      setDownloadReceiptLoader(false);
    }
  };

  const payDetailsCard = (): ReactElement => {
    return (
      <Typography
        className="payment-card-details-main-view input-header"
        component={'div'}>
        <div className="headers-values-main-view ">
          <div className="headers-view">
            {t('PAYMENTS_LOGIN.THANKS_SCREEN.AMOUNT_PAID')}
          </div>
          <div className="values-view" data-test-id="pcl-guarantor-amountPaid">
            <CurrencyFormatter
              value={guarantorPaymentInfo.amount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={'$'}
              ignoreRound={true}
              useSign={true}
            />
          </div>
        </div>
        <div className="headers-values-main-view " style={{paddingBottom: 0}}>
          <div className="headers-view">
            {t('PAYMENTS_LOGIN.THANKS_SCREEN.REFERENCE_NUMBER')}
          </div>
          <div
            className="values-view"
            data-test-id="pcl-guarantor-referenceNumber">
            {guarantorPaymentInfo.invoice_number}
          </div>
        </div>
      </Typography>
    );
  };

  const renderDownloadAndSendReceiptsUI = (): ReactElement => {
    if (
      ![PaymentTypes.PUBLIC_AD_HOC_PATIENT_PAYMENT].includes(paymentTypeRedux)
    ) {
      return (
        <div>
          <div className="email-details-container">
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <Typography className="email-info-text input-header">
                {t('PAYMENTS_LOGIN.THANKS_SCREEN.ENTER_EMAIL_ADDRESS_TEXT')}
              </Typography>
              <div className={classes.bodyContainer}>
                <div className={classes.emailContainer}>
                  {' '}
                  <FormControl
                    error={!!errors.email}
                    fullWidth={true}
                    // onChange={handleChange}
                    variant="outlined">
                    <Controller
                      id="email"
                      control={control}
                      // margin={'dense'}
                      as={PaymentsStyledInput}
                      name={'email'}
                      type={'text'}
                      value={'text'}
                      autoComplete="on"
                      placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.EMAIL')}
                      startAdornment={
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      }
                      defaultValue={guarantorDetails?.email || ''}
                      error={!!errors.email}
                      style={{height: '3.5rem'}}
                    />
                    {errors.email && (
                      <FormHelperText>{t(errors.email)}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div className={classes.buttonContainer}>
                  {paymentTypeRedux !==
                    PaymentTypes.PUBLIC_AD_HOC_PATIENT_PAYMENT && (
                    <Button
                      data-test-id="pcl-generic-sendEmail"
                      fullWidth
                      variant="contained"
                      size={'medium'}
                      color="primary"
                      className={[classes.EmailButton].join(' ')}
                      type="submit"
                      onClick={handleSubmit(handleFormSubmit)}>
                      {loader ? (
                        <ButtonLoader />
                      ) : (
                        t('PAYMENTS_LOGIN.THANKS_SCREEN.SEND_EMAIL')
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className="button-container">
            <Button
              data-test-id="pcl-generic-downloadReceipt"
              fullWidth
              variant="contained"
              size={'medium'}
              color="primary"
              className={[classes.EmailButton].join(' ')}
              type="submit"
              onClick={handleDownloadPaymentReceipt}>
              {downloadReceiptLoader ? (
                <ButtonLoader />
              ) : (
                t('PAYMENT_HISTORY.DOWNLOAD_PAYMENT_RECEIPT').toUpperCase()
              )}
            </Button>

            {!isAdmin && (
              <Button
                fullWidth
                variant="contained"
                size={'medium'}
                color="primary"
                className={[classes.EmailButton].join(' ')}
                type="submit"
                onClick={() => {
                  backNavigation();
                }}>
                {t('PAYMENTS_LOGIN.THANKS_SCREEN.GOTO_DASHBOARD')}
              </Button>
            )}
          </div>
        </div>
      );
    }
    return <div />;
  };

  const renderPaymentSectionInfo = (): ReactElement => {
    return <Typography component={'div'}>{payDetailsCard()}</Typography>;
  };

  const renderInstallmentPaymentDetails = (): ReactElement => {
    return (
      <Typography
        className="payment-card-details-main-view input-header"
        component={'div'}>
        <div className="headers-values-main-view ">
          <PayInPaymentSuccessDetails />
        </div>
      </Typography>
    );
  };

  const renderThanksScreenConfirmationContent = () => {
    const currentDate = HelperUtils.formatDisplayDate(new Date(), 'YYYY-MM-DD');

    const firstPaymentDate = installmentDetails?.instalment_start_date;

    if (currentDate === firstPaymentDate) {
      return 'INSTALLMENTS.THANK_YOU_FOR_INSTALLMENT';
    } else {
      return 'INSTALLMENTS.THANK_YOU_FOR_PAYMENT_PLAN_SET_UP';
    }
  };

  return (
    <div className={'thanks-screen-wrapper'}>
      <Card
        className={[
          'thanks-screen-content-wrap',
          isAdmin ? '' : classes.card,
          isAdmin ? '' : 'thanks-screen-border-shadow'
        ].join(' ')}>
        <CardContent className={[classes.cardPadding, 'content'].join(' ')}>
          {/* <div style={{height: '41.5rem'}}> */}
          <div className="email-check-content-view">
            {(!isAdmin ||
              paymentTypeRedux ===
                PaymentTypes.PUBLIC_AD_HOC_PATIENT_PAYMENT) && (
              <Typography className="details-view" component={'div'}>
                <HomeNavigation navigateTo={backNavigation} />
              </Typography>
            )}
            <Typography component={'div'}>
              <ThanksLogo />
            </Typography>
            <Typography className="info-text input-header" component={'div'}>
              {installmentDetails?.pay_in_installments_selected ? (
                <Trans
                  i18nKey={
                    renderThanksScreenConfirmationContent()
                    // 'INSTALLMENTS.THANK_YOU_FOR_INSTALLMENT'
                  }
                  components={[<br key={'installment'} />]}
                />
              ) : (
                <Trans
                  i18nKey={'INSTALLMENTS.THANK_YOU_FOR_PAYMENT'}
                  components={[<br key={'payment'} />]}
                />
              )}
            </Typography>

            {installmentDetails?.pay_in_installments_selected
              ? renderInstallmentPaymentDetails()
              : renderPaymentSectionInfo()}
            {guarantorPaymentInfo?.amount > 0 &&
              renderDownloadAndSendReceiptsUI()}
          </div>

          {/* </div> */}
        </CardContent>
      </Card>
    </div>
  );
};

export default ThanksScreen;
